import { action } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { tKey } from 'ember-intl';
import { type Carate, Carecommendation } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  type OverrideTypes,
  RECOMMENDATION_ACTION_STATES,
  RECOMMENDATION_SOURCES,
  type RecommendationActionPriceTypesType,
  RECOMMENDATIONACTIONS_ACTIONS,
} from 'lumen/utils/constants/pricing-assistant-constants';
import type { PricePushMeta } from 'lumen/utils/roi-trophy/types';

// Temp Carecommendation like object we use in case of user created recommendations
// We can't create a recommendationAction without having a recommendation for that day. That's why for user created recommendations we initiate the recommendationAction flow with this dummy object. Once the recommendationAction is created, we'll receive an actual carecommendation record from BE.
export type CarecommendationMock = Pick<
  Carecommendation,
  | 'action'
  | 'fromDate'
  | 'id'
  | 'overwrittenValue'
  | 'pushResultMessage'
  | 'recommendationActionId'
  | 'recommendedPmsValue'
  | 'recommendedValue'
  | 'factors'
> & {
  carate: Pick<
    Carate,
    'id' | 'pmsRoomtype' | 'rateCode' | 'value' | 'pmsValue'
  >;
};

export interface CarecommendationActionEntry {
  action: string;
  bulk_push_id: string | null;
  carate_id: string;
  currency: string;
  factors: Carecommendation['factors'];
  from_date: string;
  id: number;
  meta?: PricePushMeta;
  pms_state_text: string;
  recommendation_id: string;
  recommended_rate: number;
  source: string;
  undo_cutoff_time: string;
  user_override_rate: number | null;
}

export interface RecommendationAndCarateObject {
  carate: Carate;
  carecommendation: Carecommendation | CarecommendationMock | null;
}

export interface PricingOverridePayload {
  from_date: string;
  pms_roomtype: string;
  rate_code: string;
  subscription_id?: number;
  type: OverrideTypes;
  value: number | { maximum: number | null; minimum: number | null } | null;
}

// Contains all async actions that can be performed on a recommendation
export default class PricingAssistantHandleRecommendationActionsServiceService extends Service {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];

  @service declare roiTrophies: Services['roi-trophies'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelId() {
    return this.hotelAndMarketService.hotel?.id;
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async handleCreateRecommendationAction(
    recommendationObject: Carecommendation | CarecommendationMock,
    actionState: string,
    showToastMessage = true,
    priceType: RecommendationActionPriceTypesType | null = null,
  ) {
    if (!this.access.hasPricingAssistantEditRights) {
      return null;
    }

    // Update local recommendation action property (should feel responsive to the user)
    recommendationObject.action = actionState;

    const request =
      await this.pricingAssistantDataRequestService.createRecommendationActionTask.perform(
        recommendationObject.action,
        [recommendationObject],
        priceType,
      );

    const isRequestSuccess = request.isSuccess;
    const requestResult = request.response && request.response[0];

    if (
      requestResult &&
      isRequestSuccess &&
      requestResult.recommendationObject
    ) {
      const returnedRecommendationObject = requestResult.recommendationObject;

      // recommendationObject may or may not be carecommendation object (in case of user created recommendation, it's just an object created by us)
      // carecommendationToStartPollerFor will always be a carecommendation object. As in the case of user created recommendations, we will assign newlyAddedUserRecommendationRecord to it
      let carecommendationToStartPollerFor: Carecommendation =
        recommendationObject as Carecommendation;

      if (
        requestResult.recommendationObject.source ===
        RECOMMENDATION_SOURCES.USER_REC
      ) {
        // User recommendation flow: User can edit and push price for a day without recommendation.
        // In that case we mock a userRecommendationObject, send it to BE and manually add the returned recommendation object to the store
        // The added carate object in the newly created record ensures that the respective carate -> carecommendation link is updated automatically and no need to update pricingAssistantRatesRequestTask response
        const newlyAddedUserRecommendationRecord: Carecommendation =
          this.store.createRecord<Carecommendation>('carecommendation', {
            id: returnedRecommendationObject.recommendation_id,
            // recommendation fromDate added to the store should be local hoteltime date. As fromDate property on carecommendation model is transformed using hoteldate transform (@attr('hoteldate') fromDate)
            fromDate: this.hotelAndMarketService.toLocalTime(
              returnedRecommendationObject.from_date,
            ),
            action: returnedRecommendationObject.action,
            carate: this.store.peekRecord<Carate>(
              'carate',
              returnedRecommendationObject.carate_id,
            )!,
            currency: returnedRecommendationObject.currency,
            factors: returnedRecommendationObject.factors,
            overwrittenValue: returnedRecommendationObject.user_override_rate,
            recommendedValue: returnedRecommendationObject.recommended_rate,
            recommendationActionId: returnedRecommendationObject.id,
            pushResultMessage: returnedRecommendationObject.pms_state_text,
          });

        // Update user created recommendation's ID with the one returned by BE
        recommendationObject.id =
          returnedRecommendationObject.recommendation_id;

        // In case of the user created recommendation flow, make sure we pass our newly created local record
        carecommendationToStartPollerFor = newlyAddedUserRecommendationRecord;
      }

      // Update some important attributes of our local recommendation object with the values returned in the BE response
      // If there's a pushResultMessage filled in, then something went wrong. It's possible that BE already returns this at this moment (e.g. error due to maintenance window)
      recommendationObject.action = returnedRecommendationObject.action;
      recommendationObject.pushResultMessage =
        returnedRecommendationObject.pms_state_text;
      recommendationObject.recommendationActionId =
        returnedRecommendationObject.id;

      // Start polling for the recommendation
      this.pricingAssistantRecommendationActionPoller.addRecommendationsToFollow(
        [carecommendationToStartPollerFor],
      );

      let toastTitleTranslationKey = '';
      let toastTitle = '';
      let toastTextTranslationKey = '';

      // response indicates the recommendationAction creation went fine, so continue with followup logic (toast message, polling, updating local state)
      if (
        returnedRecommendationObject.action ===
        RECOMMENDATION_ACTION_STATES.ACCEPTED
      ) {
        toastTitleTranslationKey = tKey(
          'pricing_assistant.successful_price_send.title',
        );
        toastTitle = this.intl.t(toastTitleTranslationKey, { count: 1 });
        toastTextTranslationKey = tKey(
          'pricing_assistant.successful_price_send.text',
        );
      } else if (
        returnedRecommendationObject.action ===
        RECOMMENDATION_ACTION_STATES.SNOOZED
      ) {
        toastTextTranslationKey = tKey(
          'pricing_assistant.toast_message.snooze.text',
        );
      }

      if (toastTextTranslationKey && showToastMessage) {
        this.toast.success(
          this.intl.t(toastTextTranslationKey, {
            htmlSafe: true,
            price: carecommendationToStartPollerFor.formattedPriceToShow,
            date: carecommendationToStartPollerFor.fromDate.format(
              this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
            ),
            class: 'text-300--bold',
          }),
          {
            title: toastTitle,
            timeout: TOAST_TIMEOUT_LONG,
            showIcon: true,
          },
        );
      }
    } else if (recommendationObject instanceof Carecommendation) {
      // Restore recommendation action value in case the request failed
      recommendationObject.rollbackAttributes();
      // PRICING ASSISTANT TODO: error message?
    }

    if (
      this.access.hasTrophyHighlights &&
      isRequestSuccess &&
      request.meta?.milestone_reached
    ) {
      this.roiTrophies.setPricePushMilestone(request.meta.milestone_reached);
    }

    return {
      isRequestSuccess,
      requestResult,
    };
  }

  // Push multiple recommendations spanning single or multiple days for single room or multiple room types
  // Note: Even within open pricing, it can happen that user only push one recommendation (single room within the category), this method is able to handle that as well
  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async handleCreateRecommendationActionBulkPush(
    recommendationAndCarateObjects: RecommendationAndCarateObject[],
    actionState: string,
    newPriceValues: Record<string, number | null>,
    priceType: RecommendationActionPriceTypesType | null = null,
    override: PricingOverridePayload | null = null,
  ) {
    if (!this.access.hasPricingAssistantEditRights) {
      return null;
    }

    const objectsToBeActioned: (Carecommendation | CarecommendationMock)[] = [];

    recommendationAndCarateObjects.forEach(
      ({ carecommendation: recommendationObject, carate: carateObject }) => {
        const hasRecommendation = recommendationObject !== null;
        let objectToBeActioned: Carecommendation | CarecommendationMock;

        // @ts-expect-error: Type 'null' cannot be used as an index type.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const newPrice = newPriceValues[carateObject.id];

        // Case 1: Push system recommendation
        if (hasRecommendation) {
          // Update local recommendation action property (should feel responsive to the user)
          recommendationObject.action = actionState;

          const hasOverwrittenValue = newPrice !== undefined;

          // Case 1.2: System recommendation with overwritten value
          if (hasOverwrittenValue) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            recommendationObject.overwrittenValue = newPrice;
          }

          objectsToBeActioned.push(recommendationObject);
        } else {
          // Case 2: Push user created recommendation
          // Mock a recommendationObject in case rate pushed for no recommendation day - user recommendation
          objectToBeActioned = {
            fromDate: this.hotelAndMarketService.toLocalTime(
              carateObject.period.fromDate.format('YYYY-MM-DD'),
            ),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            overwrittenValue: newPrice ?? carateObject.value,
            action: RECOMMENDATIONACTIONS_ACTIONS.ACCEPTED,
            carate: {
              value: carateObject.value,
              id: carateObject.id,
              pmsRoomtype: carateObject.pmsRoomtype,
              rateCode: carateObject.rateCode,
            },
          } as CarecommendationMock;

          objectsToBeActioned.push(objectToBeActioned);
        }
      },
    );

    const request =
      await this.pricingAssistantDataRequestService.createRecommendationActionTask.perform(
        actionState,
        objectsToBeActioned,
        priceType,
        override,
      );

    const isRequestSuccess = request.isSuccess;
    const requestResult = request.response;
    const carecommendationsToStartPollerFor: Carecommendation[] = [];

    if (requestResult && isRequestSuccess) {
      // If no recommendation object returned, re-request data and skip further processing
      // This happens when there are no simulated recommendations and action endpoint is called only with the override payload
      if (!requestResult[0]?.recommendationObject) {
        const fromDate = override?.from_date;

        if (!fromDate) {
          return;
        }

        await this.pricingAssistantDataRequestService.reRequestDataAfterPushTask.perform(
          {
            hotelId: this.hotelId!,
            fromDate,
            endDate: fromDate,
            fetchOverrides:
              this.access.hasPricingAssistantDynamicRatePricingFeature,
          },
        );

        this.pricingAssistantRecommendationActionPoller.removeDateAffectedByOverridePush(
          fromDate,
        );

        return;
      }

      // Create carecommendation for user created recommendations
      requestResult.forEach((result) => {
        // This is the object returned by the BE, it's not the same as carecommendation object
        const returnedRecommendationObject: CarecommendationActionEntry =
          result.recommendationObject;

        if (
          returnedRecommendationObject.source ===
          RECOMMENDATION_SOURCES.USER_REC
        ) {
          // Same day can be pushed multiple times (via bulk update) and in that case we need to remove the old recommendation from the store before creating a new one
          const existingRecommendationRecord =
            this.store.peekRecord<Carecommendation>(
              'carecommendation',
              returnedRecommendationObject.recommendation_id,
            );
          if (existingRecommendationRecord) {
            existingRecommendationRecord.unloadRecord();
          }

          // User recommendation flow: User can edit and push price for a day without recommendation.
          // In that case we mock a userRecommendationObject, send it to BE and manually add the returned recommendation object to the store
          // The added carate object in the newly created record ensures that the respective carate -> carecommendation link is updated automatically and no need to update pricingAssistantRatesRequestTask response
          this.store.createRecord<Carecommendation>('carecommendation', {
            id: returnedRecommendationObject.recommendation_id,
            // recommendation fromDate added to the store should be local hoteltime date. As fromDate property on carecommendation model is transformed using hoteldate transform (@attr('hoteldate') fromDate)
            fromDate: this.hotelAndMarketService.toLocalTime(
              returnedRecommendationObject.from_date,
            ),
            action: returnedRecommendationObject.action,
            carate: this.store.peekRecord<Carate>(
              'carate',
              returnedRecommendationObject.carate_id,
            )!,
            currency: returnedRecommendationObject.currency,
            factors: returnedRecommendationObject.factors,
            overwrittenValue: returnedRecommendationObject.user_override_rate,
            recommendedValue: returnedRecommendationObject.recommended_rate,
            recommendationActionId: returnedRecommendationObject.id,
            pushResultMessage: returnedRecommendationObject.pms_state_text,
            bulkPushId: returnedRecommendationObject.bulk_push_id,
          });
        }

        const recommendationObjectInStore =
          this.store.peekRecord<Carecommendation>(
            'carecommendation',
            returnedRecommendationObject.recommendation_id,
          );

        if (recommendationObjectInStore) {
          recommendationObjectInStore.action =
            returnedRecommendationObject.action;
          recommendationObjectInStore.pushResultMessage =
            returnedRecommendationObject.pms_state_text;
          recommendationObjectInStore.recommendationActionId =
            returnedRecommendationObject.id;
          recommendationObjectInStore.bulkPushId =
            returnedRecommendationObject.bulk_push_id;

          carecommendationsToStartPollerFor.push(recommendationObjectInStore);
        }
      });

      // Start polling for the recommendations
      if (request.isBulkPush) {
        this.pricingAssistantRecommendationActionPoller.addBulkPushesToFollow(
          carecommendationsToStartPollerFor,
        );
      } else {
        this.pricingAssistantRecommendationActionPoller.addRecommendationsToFollow(
          carecommendationsToStartPollerFor,
        );
      }
    } else {
      // Restore recommendation action value in case the request failed
      carecommendationsToStartPollerFor.forEach((recommendationObject) => {
        recommendationObject.rollbackAttributes();
      });
    }

    if (
      this.access.hasTrophyHighlights &&
      isRequestSuccess &&
      request.meta?.milestone_reached
    ) {
      this.roiTrophies.setPricePushMilestone(request.meta.milestone_reached);
    }

    return {
      isRequestSuccess,
      requestResult,
    };
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async handleCreateRecommendationActionWithOverwrittenValue(
    carateObject: Carate,
    recommendationObject: Carecommendation | null,
    newPriceValue: number,
    showToastMessage = true,
    priceType: RecommendationActionPriceTypesType | null = null,
  ) {
    let objectToBeActioned: Carecommendation | CarecommendationMock;

    const hasRecommendation = recommendationObject !== null;

    // Case 1: Push system recommendation with overwritten value
    if (hasRecommendation) {
      // Update the overwritten value of system recommendation day
      recommendationObject.overwrittenValue = newPriceValue;
      objectToBeActioned = recommendationObject;
    } else {
      // Case 2: Push user created recommendation
      // Mock a recommendationObject in case rate pushed for no recommendation day - user recommendation
      objectToBeActioned = {
        fromDate: this.hotelAndMarketService.toLocalTime(
          carateObject.period.fromDate.format('YYYY-MM-DD'),
        ),
        overwrittenValue: newPriceValue,
        action: RECOMMENDATIONACTIONS_ACTIONS.ACCEPTED,
        carate: {
          value: carateObject.value,
          id: carateObject.id,
          pmsRoomtype: carateObject.pmsRoomtype,
          rateCode: carateObject.rateCode,
        },
      } as CarecommendationMock;
    }

    const pushResult = await this.handleCreateRecommendationAction(
      objectToBeActioned,
      RECOMMENDATION_ACTION_STATES.ACCEPTED,
      showToastMessage,
      priceType,
    );

    return pushResult;
  }
}

declare module '@ember/service' {
  interface Registry {
    'pricing-assistant-handle-recommendation-actions-service': PricingAssistantHandleRecommendationActionsServiceService;
  }
}
